import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import { selectFeatures } from "features/auth/auth-slice";
import {
  getTrialInfo,
  getTrialStatusMessage,
  shouldShowTrialBanner
} from "utils/trial-status.util";

export const TRIAL_ENDS_WITH_BILLING =
  "Billing will automatically begin when your trial ends.";
export const TRIAL_ENDS_WITHOUT_BILLING =
  "When your trial ends, purchase a subscription to keep access.";

interface TrialStatusBannerProps {
  featureKey?: "ai_transcription_minutes" | "ambient_scribe_users" | string;
  sx?: SxProps;
}

export default function TrialStatusBanner({
  featureKey = "ai_transcription_minutes",
  sx
}: TrialStatusBannerProps) {
  const features = useAppSelector(selectFeatures);

  const featureStatus = features?.[featureKey];

  if (!shouldShowTrialBanner(featureStatus)) {
    return null;
  }

  const trialInfo = getTrialInfo(featureStatus);
  const message = getTrialStatusMessage(trialInfo);
  // A trial with no Stripe subscription (SN-19868) ends with nothing to charge, so promising that
  // billing begins would be false. An older backend omits the field: keep the old sentence there.
  const afterTrial =
    featureStatus?.auto_billing === false
      ? TRIAL_ENDS_WITHOUT_BILLING
      : TRIAL_ENDS_WITH_BILLING;

  if (!message) {
    return null;
  }

  return (
    <Box sx={{ mb: 2, width: "100%", position: "relative", zIndex: 1, ...sx }}>
      <Alert
        severity="warning"
        variant="filled"
        sx={{
          "& .MuiAlert-message": {
            width: "100%"
          }
        }}
      >
        <AlertTitle>Trial Status</AlertTitle>
        <Typography variant="body2">
          {message}. {afterTrial}
        </Typography>
      </Alert>
    </Box>
  );
}
